/**
 * ⚙️ Settings - Asetukset ja järjestelmän tila
 *
 * Näyttää yhteyden ja palvelimen tilan sekä tarjoaa ylläpitotoiminnot
 * (yhteyden testaus, paikallisten asetusten tyhjennys, uloskirjautuminen).
 *
 * Huom: aiemmat "Socket.IO URL" ja "automaattinen uudelleenyhdistäminen"
 * -kentät poistettiin - ne olivat pelkkää käyttöliittymää, eivät vaikuttaneet
 * yhteyteen mitenkään (osoite tulee App.tsx:n SocketProviderilta).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Divider, Paper, Snackbar,
  Stack, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useSocketService } from '../hooks/useSocket';
import { useAuth } from '../hooks/useAuth';

interface SystemInfo {
  version?: string;
  uptime?: number;
  lastRestart?: string;
  activeConnections?: number;
}

/** Yksi tilaruutu (otsikko + pääarvo + lisätieto) */
const StatTile: React.FC<{
  title: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
}> = ({ title, value, hint }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: '1 1 200px', minWidth: 180 }}>
    <Typography variant="caption" color="text.secondary">{title}</Typography>
    <Box sx={{ mt: 0.5, mb: hint ? 0.5 : 0 }}>{value}</Box>
    {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
  </Paper>
);

export const Settings: React.FC = () => {
  const { service, isConnected, connectionStatus } = useSocketService();
  const { isAuthenticated, token, logout } = useAuth();
  const [systemInfo, setSystemInfo] = useState<SystemInfo>({});
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const loadSystemInfo = useCallback(async () => {
    if (!isConnected) return;
    try {
      setLoading(true);
      const response = await service.sendRequest({ type: 'get_global_data' });
      if (response.success && response.data) {
        setSystemInfo(response.data as SystemInfo);
      }
    } catch (error) {
      console.error('❌ Virhe järjestelmätietojen latauksessa:', error);
    } finally {
      setLoading(false);
    }
  }, [isConnected, service]);

  useEffect(() => { loadSystemInfo(); }, [isConnected, loadSystemInfo]);

  // Palvelimen työntämät tilapäivitykset
  useEffect(() => {
    if (!isConnected) return;
    const handleStatusUpdate = (data: Record<string, unknown>) => {
      if (data.systemInfo) {
        setSystemInfo((prev) => ({ ...prev, ...(data.systemInfo as SystemInfo) }));
      }
    };
    try {
      const socket = service.instance;
      socket.on('statusUpdate', handleStatusUpdate);
      return () => { socket.off('statusUpdate', handleStatusUpdate); };
    } catch {
      return () => {};
    }
  }, [isConnected, service]);

  const testConnection = async () => {
    try {
      setTesting(true);
      const response = await service.sendRequest({ type: 'get_controller_status' });
      setToast(response.success
        ? { msg: 'Yhteys toimii — palvelin vastaa normaalisti', ok: true }
        : { msg: `Yhteysvirhe: ${response.error || 'tuntematon virhe'}`, ok: false });
    } catch (error) {
      setToast({ msg: error instanceof Error ? error.message : 'Verkkovirhe', ok: false });
    } finally {
      setTesting(false);
    }
  };

  const clearStorage = () => {
    localStorage.clear();
    setConfirmClear(false);
    setToast({ msg: 'Tallennetut asetukset tyhjennetty — päivitä sivu', ok: true });
  };

  const formatUptime = (seconds?: number) => {
    if (!seconds) return 'Ei tiedossa';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return d > 0 ? `${d} vrk ${h} h ${m} min` : h > 0 ? `${h} h ${m} min` : `${m} min`;
  };

  if (!isConnected) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Yhdistetään palvelimeen…</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" fontWeight={700}>Asetukset</Typography>
        <Typography color="text.secondary">Yhteyden ja järjestelmän tila</Typography>
      </Box>

      {/* Tilaruudut */}
      <Stack direction="row" flexWrap="wrap" gap={2}>
        <StatTile
          title="Yhteys"
          value={
            <Chip
              size="small"
              label={isConnected ? 'Yhdistetty' : 'Ei yhteyttä'}
              color={isConnected ? 'success' : 'error'}
            />
          }
          hint={`Tila: ${connectionStatus}`}
        />
        <StatTile
          title="Kirjautuminen"
          value={
            <Chip
              size="small"
              label={isAuthenticated ? 'Kirjautunut' : 'Ei kirjautunut'}
              color={isAuthenticated ? 'success' : 'warning'}
            />
          }
          hint={token ? 'Token voimassa' : 'Ei tokenia'}
        />
        <StatTile
          title="Palvelimen käyttöaika"
          value={<Typography fontWeight={600}>{formatUptime(systemInfo.uptime)}</Typography>}
          hint={
            systemInfo.lastRestart
              ? `Käynnistetty ${new Date(systemInfo.lastRestart).toLocaleString('fi-FI')}`
              : undefined
          }
        />
        <StatTile
          title="Versio"
          value={<Typography fontWeight={600}>{systemInfo.version || 'Ei tiedossa'}</Typography>}
          hint={`Aktiivisia yhteyksiä: ${systemInfo.activeConnections ?? 0}`}
        />
      </Stack>

      {/* Toiminnot */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Toiminnot</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={16} /> : <NetworkCheckIcon />}
            onClick={testConnection}
            disabled={testing}
          >
            Testaa yhteys
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSystemInfo}
            disabled={loading}
          >
            Päivitä tiedot
          </Button>
          {isAuthenticated && (
            <Button variant="outlined" color="warning" startIcon={<LogoutIcon />} onClick={logout}>
              Kirjaudu ulos
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setConfirmClear(true)}
          >
            Tyhjennä paikalliset asetukset
          </Button>
        </Stack>
      </Paper>

      {!isAuthenticated && (
        <Alert severity="warning">
          Et ole kirjautunut. Ohjauskomennot eivät toimi ennen kirjautumista — käytä
          yläpalkin kirjautumistoimintoa.
        </Alert>
      )}

      {/* Tekniset tiedot - piilossa oletuksena, ei enää raakaa JSONia näkyvillä */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Tekniset tiedot</Typography>
        <Stack divider={<Divider />} spacing={0}>
          {[
            ['Yhteyden tila', connectionStatus],
            ['Palvelimen versio', systemInfo.version || 'Ei tiedossa'],
            ['Käyttöaika', formatUptime(systemInfo.uptime)],
            ['Aktiiviset yhteydet', String(systemInfo.activeConnections ?? 0)],
            ['Token tallennettu', token ? 'Kyllä' : 'Ei'],
          ].map(([k, v]) => (
            <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 1 }}>
              <Typography color="text.secondary">{k}</Typography>
              <Typography fontWeight={500}>{v}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
        <DialogTitle>Tyhjennetäänkö paikalliset asetukset?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tämä poistaa selaimeen tallennetut asetukset, kirjautumistokenin ja
            Koti-sivun pohjakuvan asettelun tästä laitteesta. Palvelimen tietoihin
            tämä ei vaikuta.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)}>Peruuta</Button>
          <Button color="error" onClick={clearStorage}>Tyhjennä</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.ok ? 'success' : 'error'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Settings;
